import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";
import { hasErrorCode } from "./errors";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/assistant-conversations`;
const IDEMPOTENCY_KEY_HEADER = "Idempotency-Key";
const TURN_IN_PROGRESS_ERROR_CODE = "ASSISTANT_TURN_IN_PROGRESS";
const TURN_IN_PROGRESS_RETRY_MS = 3000;
const TURN_IN_PROGRESS_RETRY_LIMIT = 70;
const CONNECTION_LOST_RETRY_LIMIT = 40;

const CONVERSATION_BUSY_ERROR_CODE = "ASSISTANT_CONVERSATION_BUSY";
const SERVICE_UNAVAILABLE_ERROR_CODE = "ASSISTANT_SERVICE_UNAVAILABLE";
const MESSAGE_REJECTED_ERROR_CODE = "ASSISTANT_MESSAGE_REJECTED";
const TOKEN_BUDGET_EXHAUSTED_ERROR_CODE = "ASSISTANT_TOKEN_BUDGET_EXHAUSTED";
const CONVERSATION_NOT_FOUND_ERROR_CODE = "ASSISTANT_CONVERSATION_NOT_FOUND";

const keys = {
  all: () => ["assistantConversations"] as const,
  byId: (conversationId: string) => [...keys.all(), conversationId] as const,
  budget: () => [...keys.all(), "budget"] as const,
  health: () => [...keys.all(), "health"] as const,
} as const;

const RecordPlanActionSchema = z.enum(["linkExisting", "createNew", "handBackToUser", "updateExisting"]);

const RecordPlanLineStatusSchema = z.enum([
  "proposed",
  "approved",
  "declined",
  "linked",
  "created",
  "committed",
  "failed",
]);

const AssistantPlanLineSchema = z.object({
  lineId: z.string(),
  formType: z.string(),
  name: z.string(),
  rationale: z.string().nullish(),
  status: RecordPlanLineStatusSchema,
  action: RecordPlanActionSchema.nullish(),
});

const AssistantPlanSchema = z.object({
  planId: z.string(),
  version: z.number().nullish(),
  status: RecordPlanLineStatusSchema,
  lines: z.array(AssistantPlanLineSchema),
});

export const REGISTRATION_FORM_TYPE = "privacyRegistration";
export const SYSTEM_FORM_TYPE = "system";
export const PARTNER_FORM_TYPE = "partner";

const AssistantDraftValueKindSchema = z.enum(["text", "yes", "no", "options", "person", "unit", "system", "partner"]);
export type AssistantDraftValueKind = z.infer<typeof AssistantDraftValueKindSchema>;

const AssistantDraftSchema = z.object({
  lineId: z.string().nullish(),
  formType: z.string().nullish(),
  name: z.string(),
  action: RecordPlanActionSchema.nullish(),
  answers: z.array(
    z.object({
      questionId: z.string(),
      label: z.string(),
      value: z.string(),
      values: z.array(z.string()).nullish(),
      section: z.string().nullish(),
      kind: AssistantDraftValueKindSchema.nullish(),
      changed: z.boolean().nullish(),
      previous: z.string().nullish(),
      previousValues: z.array(z.string()).nullish(),
    })
  ),
  units: z.array(z.string()).nullish(),
});
export type AssistantDraft = z.infer<typeof AssistantDraftSchema>;

const AssistantConversationStatusSchema = z.enum(["inProgress", "awaitingCreate", "created", "updated"]);

const AssistantConversationRecordSchema = z.object({
  formType: z.string(),
  name: z.string(),
  recordId: z.string().nullish(),
  action: RecordPlanActionSchema.nullish(),
});
export type AssistantConversationRecord = z.infer<typeof AssistantConversationRecordSchema>;

const AssistantTranscriptRecordSchema = z.object({
  formType: z.string(),
  name: z.string(),
  recordId: z.string(),
  draft: AssistantDraftSchema.nullish(),
});

const AssistantTurnUsageSchema = z.object({
  inputTokenCount: z.number(),
  cachedInputTokenCount: z.number(),
  outputTokenCount: z.number(),
  reasoningTokenCount: z.number(),
  modelCallCount: z.number(),
  weightedTokenCount: z.number(),
  durationMs: z.number().nullish(),
});

const AssistantTranscriptEntrySchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("message"),
    role: z.enum(["user", "assistant"]),
    text: z.string(),
    usage: AssistantTurnUsageSchema.nullish(),
  }),
  z.object({ kind: z.literal("recordCreated"), record: AssistantTranscriptRecordSchema }),
  z.object({ kind: z.literal("recordUpdated"), record: AssistantTranscriptRecordSchema }),
  z.object({ kind: z.literal("proposalConfirmed"), changes: z.boolean().nullish() }),
  z.object({ kind: z.literal("proposalWithdrawn") }),
  z.object({ kind: z.literal("submitted"), at: z.string().nullish() }),
]);
export type AssistantTranscriptEntry = z.infer<typeof AssistantTranscriptEntrySchema>;
export type AssistantTranscriptMessage = Extract<AssistantTranscriptEntry, { kind: "message" }>;

const AssistantConversationSchema = z.object({
  conversationId: z.string(),
  title: z.string().nullish(),
  formType: z.string(),
  entries: z.array(AssistantTranscriptEntrySchema),
  plan: AssistantPlanSchema.nullish(),
  awaitingApproval: z.string().nullish(),
  drafts: z.array(AssistantDraftSchema).nullish(),
  recordId: z.string().nullish(),
  status: AssistantConversationStatusSchema.nullish(),
  records: z.array(AssistantConversationRecordSchema).nullish(),
});
export type AssistantConversation = z.infer<typeof AssistantConversationSchema>;

export const assistantConversationQueryOptions = (conversationId: string) =>
  queryOptions({
    queryKey: keys.byId(conversationId),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${conversationId}`, { schema: AssistantConversationSchema }),
    throwOnError: error => !hasErrorCode(error, CONVERSATION_NOT_FOUND_ERROR_CODE),
    retry: (failureCount, error) => !hasErrorCode(error, CONVERSATION_NOT_FOUND_ERROR_CODE) && failureCount < 3,
  });

const AssistantConversationSummarySchema = z.object({
  conversationId: z.string(),
  title: z.string().nullish(),
  formType: z.string(),
  updatedAt: z.string(),
  recordId: z.string().nullish(),
  status: AssistantConversationStatusSchema.nullish(),
  recordStatus: z.enum(["draft", "review", "done", "completion", "audit"]).nullish(),
  records: z.array(AssistantConversationRecordSchema).nullish(),
});
export type AssistantConversationSummary = z.infer<typeof AssistantConversationSummarySchema>;

export const assistantConversationsQueryOptions = () =>
  queryOptions({
    queryKey: keys.all(),
    queryFn: () => api.get(BASE_ENDPOINT, { schema: z.array(AssistantConversationSummarySchema) }),
    throwOnError: true,
  });

export const deleteAssistantConversationMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ conversationId }: { conversationId: string }) => api.delete(`${BASE_ENDPOINT}/${conversationId}`),
    onSuccess: () => invalidateAll(),
  });

const AssistantTokenBudgetSchema = z.object({
  spentPercent: z.number(),
  resetsAt: z.string(),
  weightedTokensSpent: z.number(),
  weightedTokenLimit: z.number(),
});
export type AssistantTokenBudget = z.infer<typeof AssistantTokenBudgetSchema>;

export const assistantBudgetQueryOptions = () =>
  queryOptions({
    queryKey: keys.budget(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/budget`, { schema: AssistantTokenBudgetSchema }),
  });

export const assistantHealthQueryOptions = () =>
  queryOptions({
    queryKey: keys.health(),
    queryFn: () =>
      api.get(`${BASE_ENDPOINT}/health`, {
        schema: z.object({
          status: z.enum(["healthy", "degraded", "down"]),
          reason: z.string().nullish(),
          checkedAt: z.string(),
        }),
      }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

export const isConversationBusyError = (error: unknown) => hasErrorCode(error, CONVERSATION_BUSY_ERROR_CODE);

export const isServiceUnavailableError = (error: unknown) => hasErrorCode(error, SERVICE_UNAVAILABLE_ERROR_CODE);

export const isMessageRejectedError = (error: unknown) => hasErrorCode(error, MESSAGE_REJECTED_ERROR_CODE);

export const isBudgetExhaustedError = (
  error: unknown
): error is { errorCode: string; budget?: AssistantTokenBudget | null } =>
  hasErrorCode(error, TOKEN_BUDGET_EXHAUSTED_ERROR_CODE);

const AssistantReplySchema = z.object({
  reply: z.string(),
  plan: AssistantPlanSchema.nullish(),
  awaitingApproval: z.string().nullish(),
  drafts: z.array(AssistantDraftSchema).nullish(),
  recordId: z.string().nullish(),
  budget: AssistantTokenBudgetSchema.nullish(),
  usage: AssistantTurnUsageSchema.nullish(),
  errorCode: z.string().nullish(),
});

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const isConnectionLost = (error: unknown) => error instanceof TypeError;

const isStreamMissing = (error: unknown) =>
  typeof error === "object" && error !== null && "status" in error && error.status === 404;

const runTurn = async (endpoint: string, payload: unknown, idempotencyKey: string) => {
  let connectionsLost = 0;
  let stillInProgress = 0;

  for (;;) {
    try {
      const reply = await api.post(endpoint, payload, {
        schema: AssistantReplySchema,
        headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
      });

      if (reply.errorCode !== TURN_IN_PROGRESS_ERROR_CODE) return reply;
      if (++stillInProgress > TURN_IN_PROGRESS_RETRY_LIMIT)
        throw new Error(`The assistant turn did not finish within ${TURN_IN_PROGRESS_RETRY_LIMIT} checks`);
    } catch (error) {
      if (!isConnectionLost(error) || ++connectionsLost > CONNECTION_LOST_RETRY_LIMIT) throw error;
    }

    await wait(TURN_IN_PROGRESS_RETRY_MS);
  }
};

export type AssistantTurnStream = {
  onText: (text: string) => void;
  onTool: (toolName: string, subject?: string | null) => void;
  onStep?: (kind: AssistantCommitStepKind, subject?: string | null) => void;
};

type StreamEvent = { name: string; data: string };

async function* streamEvents(response: Response): AsyncGenerator<StreamEvent> {
  if (!response.body) return;

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffered = "";

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return;

      buffered += value.replace(/\r\n/g, "\n");
      let end = buffered.indexOf("\n\n");

      while (end !== -1) {
        const lines = buffered.slice(0, end).split("\n");
        buffered = buffered.slice(end + 2);
        end = buffered.indexOf("\n\n");

        const name = lines
          .find(line => line.startsWith("event:"))
          ?.slice("event:".length)
          .trim();
        const data = lines
          .filter(line => line.startsWith("data:"))
          .map(line => line.slice("data:".length).trimStart())
          .join("\n");

        if (name) yield { name, data };
      }
    }
  } finally {
    await reader.cancel().catch(() => undefined);
  }
}

const StreamTextSchema = z.object({ text: z.string() });
const StreamToolSchema = z.object({ name: z.string(), subject: z.string().nullish() });
const StreamStepSchema = z.object({
  kind: z.enum(["creatingRegistration", "addingRecord", "sendingForReview"]),
  subject: z.string().nullish(),
});
export type AssistantCommitStepKind = z.infer<typeof StreamStepSchema>["kind"];

const StreamOutcomeSchema = z.object({
  errorCode: z.string().nullish(),
  budget: AssistantTokenBudgetSchema.nullish(),
});

const parsedJson = (data: string): unknown => {
  try {
    return JSON.parse(data);
  } catch {
    return undefined;
  }
};

const runStreamingTurn = async (
  endpoint: string,
  payload: unknown,
  idempotencyKey: string,
  stream: AssistantTurnStream | undefined
) => {
  try {
    const response = await api.post(`${endpoint}/stream`, payload, {
      headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey, Accept: "text/event-stream" },
    });

    if (response instanceof Response) {
      for await (const event of streamEvents(response)) {
        const data = parsedJson(event.data);

        if (event.name === "text") {
          const text = StreamTextSchema.safeParse(data);
          if (text.success) stream?.onText(text.data.text);
        } else if (event.name === "tool") {
          const tool = StreamToolSchema.safeParse(data);
          if (tool.success) stream?.onTool(tool.data.name, tool.data.subject);
        } else if (event.name === "step") {
          const step = StreamStepSchema.safeParse(data);
          if (step.success) stream?.onStep?.(step.data.kind, step.data.subject);
        } else if (event.name === "reply") {
          const outcome = StreamOutcomeSchema.safeParse(data);
          if (outcome.success && outcome.data.errorCode === TURN_IN_PROGRESS_ERROR_CODE) break;
          if (outcome.success && outcome.data.errorCode) throw outcome.data;

          const reply = AssistantReplySchema.safeParse(data);
          if (!reply.success) break;

          return reply.data;
        }
      }
    }
  } catch (error) {
    if (!isConnectionLost(error) && !isStreamMissing(error)) throw error;
  }

  return runTurn(endpoint, payload, idempotencyKey);
};

type AssistantTurn = { conversationId: string; idempotencyKey: string; stream?: AssistantTurnStream };

export const sendAssistantMessageMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ conversationId, idempotencyKey, text, stream }: AssistantTurn & { text: string }) =>
      runStreamingTurn(`${BASE_ENDPOINT}/${conversationId}/messages`, { text }, idempotencyKey, stream),
    onSuccess: () => invalidateAll(),
  });

export const approveAssistantCommitMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ conversationId, idempotencyKey, stream }: AssistantTurn) =>
      runStreamingTurn(`${BASE_ENDPOINT}/${conversationId}/approvals`, {}, idempotencyKey, stream),
    onSuccess: () => invalidateAll(),
  });
